import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/store";

interface Teacher {
  name: string;
  email: string;
  phone_number: string;
  date_reg: string;
  salary: string;
  IdNumber: string;
  file_upload: File | null;
  first_name: string;
  last_name: string;
  password: string;
  password_confirm: string;
  assigned_classes: number[];
  assigned_sections: number[];
}

interface Class {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
  class_associated: number;
}

// Helper function to get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AddTeacher: React.FC = () => {
  const [teacher, setTeacher] = useState<Teacher>({
    name: "",
    email: "",
    phone_number: "",
    date_reg: getCurrentDate(), // Auto-populate with current date
    salary: "",
    IdNumber: "",
    file_upload: null,
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
    assigned_classes: [],
    assigned_sections: [],
  });

  const [classes, setClasses] = useState<Class[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [filteredSections, setFilteredSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [filePreview, setFilePreview] = useState<string | null>(null);

  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth.value);

  useEffect(() => {
    fetchClassesAndSections();
  }, []);

  useEffect(() => {
    // Filter sections based on selected classes
    if (teacher.assigned_classes.length > 0) {
      const filtered = sections.filter(section =>
        teacher.assigned_classes.includes(section.class_associated)
      );
      setFilteredSections(filtered);
    } else {
      setFilteredSections([]);
    }
  }, [teacher.assigned_classes, sections]);

  const fetchClassesAndSections = async () => {
    try {
      const [classesRes, sectionsRes] = await Promise.all([
        axios.get('/Branches/classes/'),
        axios.get('/Branches/sections/')
      ]);
      setClasses(classesRes.data.results || classesRes.data);
      setSections(sectionsRes.data);
    } catch (err) {
      console.error('Error fetching classes/sections:', err);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!teacher.name.trim()) errors.name = "Magaca waa loo baahan yahay";
    if (!teacher.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      errors.email = "Geli email sax ah";
    // Phone number validation removed - accept any format
    if (!teacher.phone_number.trim()) errors.phone_number = "Lambarka taleefanka waa loo baahan yahay";
    if (!teacher.date_reg) errors.date_reg = "Taariikhda waa loo baahan yahay";
    if (!teacher.IdNumber.trim()) errors.IdNumber = "Lambarka aqoonsiga waa loo baahan yahay";

    // User account validation
    if (!teacher.first_name.trim()) errors.first_name = "Magaca koowaad waa loo baahan yahay";
    if (!teacher.last_name.trim()) errors.last_name = "Magaca aabo waa loo baahan yahay";
    if (!teacher.password) errors.password = "Lambarka sirta waa loo baahan yahay";
    if (teacher.password.length < 4) errors.password = "Lambarka sirta waa inuu ka yaraan 4 xaraf"; // Changed from 8 to 4
    if (teacher.password !== teacher.password_confirm) {
      errors.password_confirm = "Lambarka sirta isma lahan";
    }

    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTeacher({ ...teacher, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setTeacher({ ...teacher, file_upload: file });
    setFilePreview(file ? URL.createObjectURL(file) : null);
  };

  const handleClassChange = (classId: number, checked: boolean) => {
    if (checked) {
      setTeacher({
        ...teacher,
        assigned_classes: [...teacher.assigned_classes, classId]
      });
    } else {
      setTeacher({
        ...teacher,
        assigned_classes: teacher.assigned_classes.filter(id => id !== classId),
        assigned_sections: teacher.assigned_sections.filter(sectionId => {
          const section = sections.find(s => s.id === sectionId);
          return section?.class_associated !== classId;
        })
      });
    }
  };

  const handleSectionChange = (sectionId: number, checked: boolean) => {
    if (checked) {
      setTeacher({
        ...teacher,
        assigned_sections: [...teacher.assigned_sections, sectionId]
      });
    } else {
      setTeacher({
        ...teacher,
        assigned_sections: teacher.assigned_sections.filter(id => id !== sectionId)
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setFieldErrors({});

    try {
      const formData = new FormData();

      // Teacher fields
      formData.append('name', teacher.name);
      formData.append('email', teacher.email);
      formData.append('phone_number', teacher.phone_number);
      formData.append('date_reg', teacher.date_reg);
      formData.append('salary', teacher.salary);
      formData.append('IdNumber', teacher.IdNumber);

      // User account fields
      formData.append('first_name', teacher.first_name);
      formData.append('last_name', teacher.last_name);
      formData.append('password', teacher.password);
      formData.append('create_user_account', 'true');

      // Classes and sections
      teacher.assigned_classes.forEach(classId => {
        formData.append('assigned_classes', classId.toString());
      });
      teacher.assigned_sections.forEach(sectionId => {
        formData.append('assigned_sections', sectionId.toString());
      });

      if (teacher.file_upload) {
        formData.append('file_upload', teacher.file_upload);
      }

      const apiUrl = "/Branches/teachers/";
      await axios.post(apiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setLoading(false);
      setAdded(true);
      navigate("/teachers");
    } catch (err) {
      setLoading(false);
      if (axios.isAxiosError(err)) {
        const errorData = err.response?.data;
        if (errorData?.email) {
          setError(errorData.email);
        } else {
          setError(err.response?.data?.detail || "Ku guuldareystay ku darista macalinka.");
        }
      } else {
        setError("Ku guuldareystay ku darista macalinka.");
      }
    }
  };

  if (added) return <Navigate to="/teachers" />;

  return (
    <div className="dashboard-content">
      {auth ? (
        <>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-5">
            <div>
              <h1 className="fw-bold text-dark mb-2">Diiwaangelinta Macalinka Cusub</h1>
              <p className="text-muted fs-5 mb-0">Samee profile-ka macalinka iyo ciwaanka isticmaalaha hal mar</p>
            </div>
            <button className="btn btn-outline-secondary btn-lg rounded-pill px-5" onClick={() => navigate('/teachers')}>
              Iska Daay
            </button>
          </div>

          <div className="card-modern overflow-hidden">
            <div className="bg-primary bg-opacity-10 px-5 py-4 border-bottom border-primary border-opacity-10">
              <h4 className="text-primary fw-bold mb-0">Macluumaadka Macalinka & Ciwaanka</h4>
            </div>
            <div className="card-body p-5">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show mb-4 border-0 shadow-sm">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError(null)}
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="row g-5">
                  {/* Section 1: Personal Information */}
                  <div className="col-12">
                    <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                      <i className="bi bi-person-badge me-2 text-primary"></i>Xogta Shakhsiga
                    </h5>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Magaca Oo Buuxa</label>
                        <input
                          type="text"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.name ? "is-invalid" : ""}`}
                          name="name"
                          value={teacher.name}
                          onChange={handleChange}
                          placeholder="t.usaale Axmed Cali"
                        />
                        {fieldErrors.name && <div className="invalid-feedback">{fieldErrors.name}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Lambarka Aqoonsiga</label>
                        <input
                          type="text"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.IdNumber ? "is-invalid" : ""}`}
                          name="IdNumber"
                          value={teacher.IdNumber}
                          onChange={handleChange}
                          placeholder="t.usaale ID-123456"
                        />
                        {fieldErrors.IdNumber && <div className="invalid-feedback">{fieldErrors.IdNumber}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Taariikhda Diiwaangelinta</label>
                        <input
                          type="date"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.date_reg ? "is-invalid" : ""}`}
                          name="date_reg"
                          value={teacher.date_reg}
                          onChange={handleChange}
                          max={getCurrentDate()}
                        />
                        {fieldErrors.date_reg && <div className="invalid-feedback">{fieldErrors.date_reg}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Contact & Professional */}
                  <div className="col-12">
                    <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                      <i className="bi bi-telephone me-2 text-primary"></i>Xiriirka & Xirfadda
                    </h5>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Ciwaanka Email-ka</label>
                        <input
                          type="email"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.email ? "is-invalid" : ""}`}
                          name="email"
                          value={teacher.email}
                          onChange={handleChange}
                          placeholder="axmed@tusaale.com"
                        />
                        {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
                        <small className="text-muted">Email-kan waxaa loo isticmaali doonaa gelitaanka</small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Lambarka Taleefanka</label>
                        <input
                          type="text"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.phone_number ? "is-invalid" : ""}`}
                          name="phone_number"
                          value={teacher.phone_number}
                          onChange={handleChange}
                          placeholder="061XXXXX"
                        />
                        {fieldErrors.phone_number && <div className="invalid-feedback">{fieldErrors.phone_number}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Cadadka Mushaarka</label>
                        <div className="input-group input-group-lg">
                          <span className="input-group-text bg-light border-0 text-secondary fw-bold">KSH</span>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control bg-light border-0"
                            name="salary"
                            value={teacher.salary}
                            onChange={handleChange}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Soo Geli Dukumenti</label>
                        <input
                          type="file"
                          className="form-control form-control-lg bg-light border-0"
                          name="file_upload"
                          onChange={handleFileChange}
                        />
                        {filePreview && (
                          <div className="mt-3 p-2 border rounded bg-white d-inline-block shadow-sm">
                            <small className="d-block text-muted mb-1 small">Preview:</small>
                            <img
                              src={filePreview}
                              alt="preview"
                              className="rounded"
                              style={{ maxHeight: "100px", objectFit: 'cover' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 3: User Account Credentials */}
                  <div className="col-12">
                    <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                      <i className="bi bi-shield-lock me-2 text-primary"></i>Xogta Gelitaanka Ciwaanka
                    </h5>
                    <div className="alert alert-info border-0 mb-4">
                      <i className="bi bi-info-circle me-2"></i>
                      Ciwaanka isticmaalaha si toos ah ayaa loo sameyn doonaa iyadoo la siinayo doorka 'Macalin'
                    </div>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Magaca Koowaad</label>
                        <input
                          type="text"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.first_name ? "is-invalid" : ""}`}
                          name="first_name"
                          value={teacher.first_name}
                          onChange={handleChange}
                          placeholder="Koowaad"
                        />
                        {fieldErrors.first_name && <div className="invalid-feedback">{fieldErrors.first_name}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Magaca Aabo</label>
                        <input
                          type="text"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.last_name ? "is-invalid" : ""}`}
                          name="last_name"
                          value={teacher.last_name}
                          onChange={handleChange}
                          placeholder="Labaad"
                        />
                        {fieldErrors.last_name && <div className="invalid-feedback">{fieldErrors.last_name}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Lambarka Sirta</label>
                        <input
                          type="password"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.password ? "is-invalid" : ""}`}
                          name="password"
                          value={teacher.password}
                          onChange={handleChange}
                          placeholder="Ugu yaraan 4 xaraf"
                        />
                        {fieldErrors.password && <div className="invalid-feedback">{fieldErrors.password}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">Xaqiiji Lambarka Sirta</label>
                        <input
                          type="password"
                          className={`form-control form-control-lg bg-light border-0 ${fieldErrors.password_confirm ? "is-invalid" : ""}`}
                          name="password_confirm"
                          value={teacher.password_confirm}
                          onChange={handleChange}
                          placeholder="Dib u geli lambarka sirta"
                        />
                        {fieldErrors.password_confirm && <div className="invalid-feedback">{fieldErrors.password_confirm}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Class & Section Assignment */}
                  <div className="col-12">
                    <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                      <i className="bi bi-door-open me-2 text-primary"></i>Fasalka & Qeybta Loo Diray
                    </h5>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">U Dir Fasalada</label>
                        <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {classes.map(cls => (
                            <div key={cls.id} className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`class-${cls.id}`}
                                checked={teacher.assigned_classes.includes(cls.id)}
                                onChange={(e) => handleClassChange(cls.id, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`class-${cls.id}`}>
                                {cls.name}
                              </label>
                            </div>
                          ))}
                          {classes.length === 0 && (
                            <p className="text-muted mb-0">Fasal lama hayo</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-secondary">U Dir Qeybaha</label>
                        <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {filteredSections.map(section => (
                            <div key={section.id} className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`section-${section.id}`}
                                checked={teacher.assigned_sections.includes(section.id)}
                                onChange={(e) => handleSectionChange(section.id, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`section-${section.id}`}>
                                {section.name}
                              </label>
                            </div>
                          ))}
                          {filteredSections.length === 0 && teacher.assigned_classes.length > 0 && (
                            <p className="text-muted mb-0">Qeybo looma hayo fasalada la doortay</p>
                          )}
                          {teacher.assigned_classes.length === 0 && (
                            <p className="text-muted mb-0">Horta dooro fasalada</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mt-5">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg rounded-pill px-5 w-100 shadow hover-scale fw-bold fs-5"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating Teacher & Account...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-person-plus-fill me-2"></i> Diiwaangeli Macalinka & Samee Ciwaan
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <p className="text-muted">Hubinta aqoonsiga...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTeacher;
